import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Learning Center Page
 * Interactive tutorials with completion badges
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useTour } from '../../contexts/TourContext';
import { apiClient } from '../../utils/apiClient';
import { RoleLevel } from '../../types/rbacImproved';
import { TutorialCategory, BadgeTier, ChapterUpdateType, TUTORIAL_CATEGORY_CONFIG } from '../../types/learningCenter';
import { TOURS } from '../../data/tours';
import { TutorialsGrid, TutorialCardDisplay } from '../../components/Tutorial/Card';
import { FaRocket, FaUserShield, FaSearch, FaCertificate, FaClipboardList, FaStar, FaTools } from 'react-icons/fa';
// New styled components from theme system
import { PageWrapper, HeroSection, HeroTitle, HeroSubtitle, StatsGrid, StatCard, MainContent, SectionTitle, AdminSectionTitle, SectionDescription, CategorySection, CategoryHeader } from './LearningCenter.styles';
const ICON_MAP = {
    FaRocket,
    FaSearch,
    FaCertificate,
    FaClipboardList,
    FaStar,
    FaTools,
    FaUserShield
};
// Helper function to check if user is admin
const isAdminUser = (roleLevel) => {
    return roleLevel >= RoleLevel.SALES_MANAGER; // 7 and above are admin roles
};
const LearningCenter = () => {
    const { user, isAuthenticated } = useAuthStore();
    const userRoleLevel = user?.roleLevel || RoleLevel.GUEST;
    const { startTour, hasCompletedTour, showChapterCompleteModal } = useTour();
    const [progress, setProgress] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');
    // Load user progress from profile
    const loadProgress = async () => {
        if (!isAuthenticated || !user) {
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            // Get profile data which includes tutorial progress
            const response = await apiClient.get('auth-profile');
            console.log('📥 Profile API response:', response);
            console.log('📥 Profile data:', response.data);
            console.log('📥 Profile nested:', response.data?.profile?.profile);
            console.log('📥 Profile tutorials:', response.data?.profile?.profile?.tutorials);
            if (response.ok && response.data?.success) {
                const tutorialData = response.data.profile?.profile?.tutorials;
                if (tutorialData) {
                    console.log('✅ Found tutorial data in profile:', tutorialData);
                    // Convert profile tutorials to UserTutorialProgress format
                    setProgress({
                        userId: user.id,
                        chapterProgress: tutorialData.chapterProgress || {},
                        completedTutorials: tutorialData.completedTutorials || [],
                        earnedBadges: tutorialData.earnedBadges || [],
                        inProgressTutorials: [],
                        totalCompletionPercentage: tutorialData.totalCompletionPercentage || 0,
                        lastActivityDate: tutorialData.lastActivityDate || new Date().toISOString()
                    });
                }
                else {
                    console.log('ℹ️ No tutorial data in profile yet - user has not completed any tutorials');
                    setProgress({
                        userId: user.id,
                        chapterProgress: {},
                        completedTutorials: [],
                        earnedBadges: [],
                        inProgressTutorials: [],
                        totalCompletionPercentage: 0,
                        lastActivityDate: new Date().toISOString()
                    });
                }
            }
        }
        catch (error) {
            console.error('Error loading tutorial progress from profile:', error);
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        loadProgress();
    }, [isAuthenticated]);
    // Check if tutorial is completed (chapter-based)
    const isTutorialCompleted = (tutorial) => {
        const tutorialId = typeof tutorial === 'string' ? tutorial : tutorial.id;
        // If tutorial object provided, check chapter progress (authoritative source)
        if (typeof tutorial !== 'string') {
            const tutorialChapters = progress?.chapterProgress?.[tutorial.id];
            if (tutorialChapters && tutorial.chapters.length > 0) {
                const completedChapters = tutorial.chapters.filter(ch => tutorialChapters[ch.id]?.completed);
                const allComplete = completedChapters.length === tutorial.chapters.length;
                console.log(`🔍 Tutorial ${tutorialId} chapter progress: ${completedChapters.length}/${tutorial.chapters.length} = ${allComplete}`);
                return allComplete;
            }
            // No chapter progress yet
            console.log(`🔍 Tutorial ${tutorialId} - no chapter progress:`, false);
            return false;
        }
        // Legacy string check - check completedTutorials array
        if (progress?.completedTutorials.includes(tutorialId)) {
            console.log(`🔍 Tutorial ${tutorialId} completed (legacy array):`, true);
            return true;
        }
        console.log(`🔍 Tutorial ${tutorialId} completed:`, false);
        return false;
    };
    // Check if tutorial is locked
    const isTutorialLocked = (tutorial) => {
        // Check role level
        if (userRoleLevel < tutorial.minRoleLevel) {
            return true;
        }
        // Check prerequisites
        if (tutorial.requiredTutorials && tutorial.requiredTutorials.length > 0) {
            return !tutorial.requiredTutorials.every(reqId => isTutorialCompleted(reqId));
        }
        return false;
    };
    // Mark tutorial as complete and award badge
    const markTutorialComplete = async (tutorial) => {
        console.log('🎓 markTutorialComplete called for:', tutorial.title);
        if (!user) {
            console.error('❌ No user found - cannot mark tutorial complete');
            return;
        }
        try {
            // Get current completed tutorials and badges
            const currentCompleted = progress?.completedTutorials || [];
            const currentBadges = progress?.earnedBadges || [];
            console.log('📊 Current completed tutorials:', currentCompleted);
            console.log('🏅 Current badges:', currentBadges);
            // Add new tutorial if not already completed
            if (!currentCompleted.includes(tutorial.id)) {
                console.log('✅ Tutorial not yet completed - awarding badge');
                const updatedCompleted = [...currentCompleted, tutorial.id];
                const newBadge = {
                    ...tutorial.badge,
                    earnedDate: new Date().toISOString()
                };
                const updatedBadges = [...currentBadges, newBadge];
                // Calculate new completion percentage
                const totalCompletionPercentage = Math.round((updatedCompleted.length / MOCK_TUTORIALS.length) * 100);
                console.log('📤 Updating profile with:', {
                    completedTutorials: updatedCompleted,
                    earnedBadges: updatedBadges,
                    totalCompletionPercentage
                });
                // Update profile via auth-profile API
                const response = await apiClient.put('auth-profile', {
                    profile: {
                        tutorials: {
                            completedTutorials: updatedCompleted,
                            earnedBadges: updatedBadges,
                            totalCompletionPercentage,
                            lastActivityDate: new Date().toISOString()
                        }
                    }
                });
                console.log('📥 Profile update response:', response);
                if (response.ok) {
                    console.log('✅ Profile updated successfully - reloading progress');
                    // Reload progress to update UI
                    await loadProgress();
                }
                else {
                    console.error('❌ Profile update failed:', response.error);
                }
            }
            else {
                console.log('ℹ️ Tutorial already completed - skipping badge award');
            }
        }
        catch (error) {
            console.error('❌ Error marking tutorial as complete:', error);
        }
    };
    // Mark chapter as complete and progress to next chapter or award badge
    const markChapterComplete = async (tutorial, chapterId) => {
        if (!user) {
            console.error('❌ No user found - cannot mark chapter complete');
            return;
        }
        console.log('📖 Marking chapter complete:', chapterId);
        // CRITICAL: Fetch LATEST chapter progress from API to avoid overwriting
        console.log('📡 Fetching latest chapter progress from API before saving...');
        const profileResponse = await apiClient.get('auth-profile');
        const latestChapterProgress = profileResponse.data?.profile?.profile?.tutorials?.chapterProgress || {};
        const latestTutorialChapters = latestChapterProgress[tutorial.id] || {};
        console.log('📊 Latest chapter progress from API:', latestTutorialChapters);
        // Find the chapter
        const chapter = tutorial.chapters.find(c => c.id === chapterId);
        if (!chapter) {
            console.error('❌ Chapter not found:', chapterId);
            return;
        }
        // Mark this chapter as complete (merge with existing!)
        const updatedChapterProgress = {
            ...latestChapterProgress, // Start with ALL existing progress
            [tutorial.id]: {
                ...latestTutorialChapters, // Keep existing tutorial chapters
                [chapterId]: {
                    chapterId: chapterId,
                    completed: true,
                    completedVersion: chapter.version,
                    completedDate: new Date().toISOString(),
                    currentVersion: chapter.version,
                    needsReview: false,
                    updateType: ChapterUpdateType.NONE
                }
            }
        };
        console.log('📝 Updated chapter progress to save:', updatedChapterProgress[tutorial.id]);
        // Check if all chapters are complete
        const allChaptersComplete = tutorial.chapters.every(ch => updatedChapterProgress[tutorial.id]?.[ch.id]?.completed);
        console.log(`📊 Chapter progress: ${Object.keys(updatedChapterProgress[tutorial.id]).filter(k => updatedChapterProgress[tutorial.id][k].completed).length}/${tutorial.chapters.length}`);
        if (allChaptersComplete) {
            console.log('🎉 ALL CHAPTERS COMPLETE - Awarding badge!');
            // Use latest data from API (fetched above) to avoid overwriting
            const latestTutorialData = profileResponse.data?.profile?.profile?.tutorials;
            const latestCompletedTutorials = latestTutorialData?.completedTutorials || [];
            const latestEarnedBadges = latestTutorialData?.earnedBadges || [];
            // Award badge for completing entire tutorial
            const completedTutorials = [...latestCompletedTutorials, tutorial.id];
            const earnedBadges = [
                ...latestEarnedBadges,
                {
                    ...tutorial.badge,
                    earnedDate: new Date().toISOString(),
                    version: tutorial.version
                }
            ];
            // Calculate completion percentage
            const totalCompletionPercentage = Math.round((completedTutorials.length / MOCK_TUTORIALS.length) * 100);
            console.log('🏅 Awarding badge:', tutorial.badge.name);
            console.log('📊 New completed tutorials:', completedTutorials);
            console.log('📊 New earned badges:', earnedBadges);
            // Update profile
            const response = await apiClient.put('auth-profile', {
                profile: {
                    tutorials: {
                        chapterProgress: updatedChapterProgress,
                        completedTutorials,
                        earnedBadges,
                        totalCompletionPercentage,
                        lastActivityDate: new Date().toISOString()
                    }
                }
            });
            if (response.ok) {
                console.log('✅ Badge awarded! Reloading progress...');
                await loadProgress();
                // Show tutorial complete modal (all chapters done!)
                showChapterCompleteModal({
                    tutorialTitle: tutorial.title,
                    completedChapter: chapter.title,
                    nextChapter: null, // No next chapter - all done!
                    completedCount: tutorial.chapters.length,
                    totalCount: tutorial.chapters.length,
                    badgeEarned: {
                        name: tutorial.badge.name,
                        tier: tutorial.badge.tier
                    },
                    onContinue: () => {
                        console.log('✅ Tutorial complete - navigating to Learning Center');
                        // Navigate back to Learning Center
                        window.location.href = '/learning-center';
                    }
                });
            }
        }
        else {
            // Not all chapters complete - just save chapter progress
            console.log('📚 More chapters remaining - saving progress');
            // Use latest data from API (fetched above) to avoid overwriting
            const latestTutorialData = profileResponse.data?.profile?.profile?.tutorials;
            const latestCompletedTutorials = latestTutorialData?.completedTutorials || [];
            const latestEarnedBadges = latestTutorialData?.earnedBadges || [];
            const latestCompletionPercentage = latestTutorialData?.totalCompletionPercentage || 0;
            console.log('📊 Using latest data from API - completed tutorials:', latestCompletedTutorials);
            console.log('📊 Using latest data from API - earned badges:', latestEarnedBadges);
            // Update profile with chapter progress (merging with latest!)
            const response = await apiClient.put('auth-profile', {
                profile: {
                    tutorials: {
                        chapterProgress: updatedChapterProgress, // Our new chapter progress
                        completedTutorials: latestCompletedTutorials, // Latest from API
                        earnedBadges: latestEarnedBadges, // Latest from API
                        totalCompletionPercentage: latestCompletionPercentage, // Latest from API
                        lastActivityDate: new Date().toISOString()
                    }
                }
            });
            if (response.ok) {
                console.log('✅ Chapter progress saved!');
                // Find next chapter using the updatedChapterProgress we just calculated
                const completedChapterIds = Object.keys(updatedChapterProgress[tutorial.id]).filter(k => updatedChapterProgress[tutorial.id][k].completed);
                console.log('📊 Completed chapter IDs:', completedChapterIds);
                console.log('📊 All tutorial chapters:', tutorial.chapters.map(c => c.id));
                const nextChapter = tutorial.chapters.find(ch => !completedChapterIds.includes(ch.id));
                console.log('📖 Next chapter found:', nextChapter?.title || 'none');
                if (nextChapter) {
                    console.log('⏸️ Showing GLOBAL chapter complete modal');
                    // Reload progress
                    await loadProgress();
                    // Show global modal (rendered in TourContext, visible on ANY page)
                    showChapterCompleteModal({
                        tutorialTitle: tutorial.title,
                        completedChapter: chapter.title,
                        nextChapter: { id: nextChapter.id, title: nextChapter.title, tour: nextChapter.tour },
                        completedCount: completedChapterIds.length,
                        totalCount: tutorial.chapters.length,
                        onContinue: () => {
                            console.log('▶️ User clicked Continue - starting next chapter:', nextChapter.title);
                            setTimeout(() => {
                                startTour(nextChapter.tour, () => {
                                    markChapterComplete(tutorial, nextChapter.id);
                                });
                            }, 500);
                        }
                    });
                }
                else {
                    console.log('⚠️ No next chapter found - all chapters should be complete');
                    // Still reload for final chapter
                    await loadProgress();
                }
            }
        }
    };
    // Start tutorial (chapter-based)
    const handleStartTutorial = async (tutorial) => {
        if (isTutorialLocked(tutorial)) {
            return;
        }
        console.log('🚀 Starting tutorial:', tutorial.title);
        console.log('🎯 Tutorial ID:', tutorial.id);
        // Find first incomplete chapter
        const userChapters = progress?.chapterProgress?.[tutorial.id] || {};
        const firstIncompleteChapter = tutorial.chapters.find(chapter => !userChapters[chapter.id]?.completed);
        if (!firstIncompleteChapter) {
            console.log('✅ All chapters complete - restarting from first chapter');
            // All complete, restart from beginning
            startTour(tutorial.chapters[0].tour, () => {
                console.log('🎉 Chapter replayed');
            });
            return;
        }
        console.log('📖 Starting chapter:', firstIncompleteChapter.title);
        console.log('📖 Chapter ID:', firstIncompleteChapter.id);
        // Start the chapter's tour with chapter completion handler
        startTour(firstIncompleteChapter.tour, () => {
            console.log('🎉 CHAPTER COMPLETE:', firstIncompleteChapter.title);
            markChapterComplete(tutorial, firstIncompleteChapter.id);
        });
    };
    // Calculate stats
    const stats = useMemo(() => {
        const completedCount = MOCK_TUTORIALS.filter(t => isTutorialCompleted(t)).length;
        const totalBadges = progress?.earnedBadges.length || 0;
        return {
            completed: completedCount,
            total: MOCK_TUTORIALS.length,
            percentage: Math.round((completedCount / MOCK_TUTORIALS.length) * 100),
            badges: totalBadges
        };
    }, [progress, MOCK_TUTORIALS]);
    // Filter tutorials based on user role
    const visibleTutorials = useMemo(() => {
        const userIsAdmin = isAdminUser(userRoleLevel);
        return MOCK_TUTORIALS.filter(tutorial => {
            // Admins see everything
            if (userIsAdmin)
                return true;
            // Regular users only see non-admin tutorials they have access to
            return !tutorial.isAdminOnly && userRoleLevel >= tutorial.minRoleLevel;
        });
    }, [userRoleLevel]);
    // Filter by selected category
    const filteredTutorials = useMemo(() => {
        if (selectedCategory === 'all') {
            return visibleTutorials;
        }
        return visibleTutorials.filter(t => t.category === selectedCategory);
    }, [selectedCategory, visibleTutorials]);
    // Separate admin and user tutorials
    const adminTutorials = useMemo(() => {
        return filteredTutorials.filter(t => t.isAdminOnly);
    }, [filteredTutorials]);
    const userTutorials = useMemo(() => {
        return filteredTutorials.filter(t => !t.isAdminOnly);
    }, [filteredTutorials]);
    // Group user tutorials by category
    const userTutorialsByCategory = useMemo(() => {
        const grouped = {};
        userTutorials.forEach(tutorial => {
            if (!grouped[tutorial.category]) {
                grouped[tutorial.category] = [];
            }
            grouped[tutorial.category].push(tutorial);
        });
        return grouped;
    }, [userTutorials]);
    // Group admin tutorials by category
    const adminTutorialsByCategory = useMemo(() => {
        const grouped = {};
        adminTutorials.forEach(tutorial => {
            if (!grouped[tutorial.category]) {
                grouped[tutorial.category] = [];
            }
            grouped[tutorial.category].push(tutorial);
        });
        return grouped;
    }, [adminTutorials]);
    return (_jsxs(PageWrapper, { children: [_jsxs(HeroSection, { children: [_jsxs(HeroTitle, { children: ["Learning ", _jsx("span", { children: "Center" })] }), _jsx(HeroSubtitle, { children: "Complete interactive tutorials to master the Royal Gems Platform and earn badges" })] }), _jsxs(StatsGrid, { id: "tour-progress-overview", children: [_jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [stats.completed, "/", stats.total] }), _jsx("div", { className: "stat-label", children: "Tutorials Completed" })] }), _jsxs(StatCard, { "$highlighted": stats.percentage === 100, children: [_jsxs("div", { className: "stat-value", children: [stats.percentage, "%"] }), _jsx("div", { className: "stat-label", children: "Progress" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: stats.badges }), _jsx("div", { className: "stat-label", children: "Badges Earned" })] })] }), _jsxs(MainContent, { children: [isAdminUser(userRoleLevel) && adminTutorials.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(AdminSectionTitle, { children: [_jsx(FaUserShield, {}), " Admin Training"] }), _jsx(SectionDescription, { children: "Administrative tutorials for platform management and system configuration" }), Object.entries(adminTutorialsByCategory).map(([category, tutorials]) => {
                                const categoryConfig = TUTORIAL_CATEGORY_CONFIG[category];
                                const CategoryIcon = ICON_MAP[categoryConfig.icon];
                                return (_jsxs(CategorySection, { children: [_jsxs(CategoryHeader, { "$color": categoryConfig.color, children: [CategoryIcon && _jsx(CategoryIcon, {}), _jsx("h3", { children: categoryConfig.label }), _jsx("p", { children: categoryConfig.description })] }), _jsx(TutorialsGrid, { children: tutorials.map((tutorial, index) => (_jsx(TutorialCardDisplay, { tutorial: tutorial, completed: isTutorialCompleted(tutorial), locked: isTutorialLocked(tutorial), comingSoon: tutorial.comingSoon || tutorial.totalChapters === 0, userRoleLevel: userRoleLevel, onStart: handleStartTutorial, cardId: index === 0 ? 'tour-tutorial-card' : undefined }, tutorial.id))) })] }, category));
                            })] })), userTutorials.length > 0 && (_jsxs(_Fragment, { children: [_jsxs(SectionTitle, { id: "tour-platform-training", children: [_jsx(FaRocket, {}), " Platform Training"] }), _jsx(SectionDescription, { children: "Learn the features and capabilities of the Royal Gems Platform" }), Object.entries(userTutorialsByCategory).map(([category, tutorials]) => {
                                const categoryConfig = TUTORIAL_CATEGORY_CONFIG[category];
                                const CategoryIcon = ICON_MAP[categoryConfig.icon];
                                return (_jsxs(CategorySection, { children: [_jsxs(CategoryHeader, { "$color": categoryConfig.color, children: [CategoryIcon && _jsx(CategoryIcon, {}), _jsx("h3", { children: categoryConfig.label }), _jsx("p", { children: categoryConfig.description })] }), _jsx(TutorialsGrid, { children: tutorials.map((tutorial, index) => (_jsx(TutorialCardDisplay, { tutorial: tutorial, completed: isTutorialCompleted(tutorial), locked: isTutorialLocked(tutorial), comingSoon: tutorial.comingSoon || tutorial.totalChapters === 0, userRoleLevel: userRoleLevel, onStart: handleStartTutorial, cardId: index === 0 ? 'tour-tutorial-card' : undefined }, tutorial.id))) })] }, category));
                            })] }))] })] }));
};
// Mock tutorials for development - Bundled Structure
const MOCK_TUTORIALS = [
    // ========================================
    // COMPLETED TUTORIALS (Available Now)
    // ========================================
    // 1. Platform Essentials Bundle - CHAPTER-BASED STRUCTURE
    {
        id: 'platform-essentials',
        title: 'Platform Essentials',
        description: 'Master the fundamentals: certificate verification, diamond browsing, and navigation. Perfect for getting started with the Royal Gems Platform.',
        category: TutorialCategory.GETTING_STARTED,
        difficulty: 'beginner',
        estimatedMinutes: 2,
        minRoleLevel: RoleLevel.BASIC_DEALER,
        isAdminOnly: false,
        version: '1.0',
        lastUpdated: '2025-10-23T00:00:00.000Z',
        // Chapter-based structure
        chapters: [
            // Chapter 1: Certificate Verification
            {
                id: 'cert-verification',
                title: 'Certificate Verification',
                description: 'Learn how to verify diamond certificates directly from official lab websites',
                version: '1.0',
                lastUpdated: '2025-10-23T00:00:00.000Z',
                tour: TOURS.CERTIFICATE_VERIFICATION,
                estimatedMinutes: 1,
                changeLog: [
                    {
                        version: '1.0',
                        date: '2025-10-23',
                        changes: ['Initial release', 'Added support for GIA, AGS, IGI, HRD, EGL USA, GCAL'],
                        updateType: ChapterUpdateType.NONE
                    }
                ]
            },
            // Chapter 2: Diamond Browsing & Navigation
            {
                id: 'diamond-browsing',
                title: 'Diamond Browsing & Navigation',
                description: 'Master the pagination system and grid navigation for efficient browsing',
                version: '1.0',
                lastUpdated: '2025-10-23T00:00:00.000Z',
                tour: TOURS.DIAMOND_BROWSING,
                estimatedMinutes: 0.5,
                changeLog: [
                    {
                        version: '1.0',
                        date: '2025-10-23',
                        changes: ['Initial release', 'Sticky pagination sidebar', 'Quick page navigation'],
                        updateType: ChapterUpdateType.NONE
                    }
                ]
            },
            // Chapter 3: Basic Diamond Information
            {
                id: 'basic-diamond-info',
                title: 'Basic Diamond Information',
                description: 'Understand the 4Cs (Carat, Color, Clarity, Cut) and how to read diamond specifications',
                version: '1.0',
                lastUpdated: '2025-10-23T00:00:00.000Z',
                tour: TOURS.BASIC_DIAMOND_INFO,
                estimatedMinutes: 0.5,
                changeLog: [
                    {
                        version: '1.0',
                        date: '2025-10-23',
                        changes: ['Initial release', 'Covers 4Cs fundamentals', 'Price and stock status'],
                        updateType: ChapterUpdateType.NONE
                    }
                ]
            }
        ],
        totalSteps: 14, // 4 + 4 + 6 = 14 steps across all chapters
        totalChapters: 3, // Complete 3-chapter course
        comingSoon: false, // Available now!
        featured: true,
        new: true,
        badge: {
            id: 'platform-explorer-badge',
            name: 'Platform Explorer',
            description: 'Mastered platform essentials',
            icon: 'FaRocket',
            tier: BadgeTier.BRONZE,
            category: TutorialCategory.GETTING_STARTED
        }
    },
    // 2. Memo System Basics
    {
        id: 'memo-system-basics',
        title: 'Memo System Basics',
        description: 'Learn to request memos, track status updates, and manage active memo requests efficiently.',
        category: TutorialCategory.MEMO_SYSTEM,
        difficulty: 'beginner',
        estimatedMinutes: 6,
        minRoleLevel: RoleLevel.BASIC_DEALER,
        isAdminOnly: false,
        version: '1.0',
        lastUpdated: '2025-10-29T00:00:00.000Z',
        // Chapter-based structure
        chapters: [
            // Chapter 1: Requesting a Memo
            {
                id: 'memo-request',
                title: 'Requesting a Memo',
                description: 'Learn how to request diamonds on memo for client viewing',
                version: '1.0',
                lastUpdated: '2025-10-29T00:00:00.000Z',
                tour: TOURS.MEMO_REQUEST,
                estimatedMinutes: 2,
                changeLog: [
                    {
                        version: '1.0',
                        date: '2025-10-29',
                        changes: ['Initial release', 'Request memo form walkthrough', 'Client information and purpose selection'],
                        updateType: ChapterUpdateType.NONE
                    }
                ]
            },
            // Chapter 2: Tracking Memo Status
            {
                id: 'memo-status',
                title: 'Tracking Memo Status',
                description: 'View and monitor all your active memo requests in the dashboard',
                version: '1.0',
                lastUpdated: '2025-10-29T00:00:00.000Z',
                tour: TOURS.MEMO_STATUS,
                estimatedMinutes: 2,
                changeLog: [
                    {
                        version: '1.0',
                        date: '2025-10-29',
                        changes: ['Initial release', 'Dashboard overview', 'Search and sort features', 'Due date tracking'],
                        updateType: ChapterUpdateType.NONE
                    }
                ]
            },
            // Chapter 3: Managing Your Memos
            {
                id: 'memo-management',
                title: 'Managing Your Memos',
                description: 'View memo details, cancel pending requests, and track your diamond loans',
                version: '1.1',
                lastUpdated: '2025-12-28T00:00:00.000Z',
                tour: TOURS.MEMO_MANAGEMENT,
                estimatedMinutes: 2,
                changeLog: [
                    {
                        version: '1.1',
                        date: '2025-12-28',
                        changes: ['Updated for new memo dashboard', 'Focus on viewing details and canceling requests'],
                        updateType: ChapterUpdateType.MINOR
                    },
                    {
                        version: '1.0',
                        date: '2025-10-29',
                        changes: ['Initial release'],
                        updateType: ChapterUpdateType.NONE
                    }
                ]
            }
        ],
        totalSteps: 14, // 6 + 4 + 4 = 14 steps across all chapters
        totalChapters: 3, // Complete 3-chapter course
        comingSoon: false, // Available now!
        featured: true,
        new: true,
        badge: {
            id: 'memo-pro-badge',
            name: 'Memo Pro',
            description: 'Mastered memo request system',
            icon: 'FaClipboardList',
            tier: BadgeTier.SILVER,
            category: TutorialCategory.MEMO_SYSTEM
        }
    },
    // ========================================
    // ADMIN-ONLY TUTORIALS
    // ========================================
    // Financial Hub Training
    {
        id: 'financial-hub-training',
        title: 'Financial Hub Training',
        description: 'Master the Financial Hub: Dashboard analytics, supplier management, purchase tracking, payments, memos, invoices, and overdue reports.',
        category: TutorialCategory.ADMIN_ONLY,
        difficulty: 'intermediate',
        estimatedMinutes: 10,
        minRoleLevel: RoleLevel.SALES_MANAGER,
        isAdminOnly: true,
        version: '1.0',
        lastUpdated: '2025-11-09T00:00:00.000Z',
        chapters: [
            {
                id: 'financial-hub-overview',
                title: 'Financial Hub Overview',
                description: 'Explore the unified Financial Hub with Executive Dashboard and 7 integrated tabs',
                version: '1.0',
                lastUpdated: '2025-11-09T00:00:00.000Z',
                tour: TOURS.FINANCIAL_HUB_SHOWCASE,
                estimatedMinutes: 5
            },
            {
                id: 'executive-dashboard',
                title: 'Executive Command Center',
                description: 'Master the Executive Dashboard with metrics, charts, and inline actions',
                version: '1.0',
                lastUpdated: '2025-11-09T00:00:00.000Z',
                tour: TOURS.EXECUTIVE_DASHBOARD_SHOWCASE,
                estimatedMinutes: 5
            }
        ],
        totalSteps: 13,
        totalChapters: 2,
        comingSoon: false,
        featured: true,
        new: true,
        badge: {
            id: 'financial-manager-badge',
            name: 'Financial Manager',
            description: 'Mastered Financial Hub operations',
            icon: 'FaDollarSign',
            tier: BadgeTier.GOLD,
            category: TutorialCategory.ADMIN_ONLY
        }
    },
    // 4. Inventory Hub Training
    {
        id: 'inventory-hub-training',
        title: 'Inventory Hub Training',
        description: 'Master the Inventory Hub: Dashboard analytics, diamond management, and RAPNET CSV import/export operations.',
        category: TutorialCategory.ADMIN_ONLY,
        difficulty: 'intermediate',
        estimatedMinutes: 8,
        minRoleLevel: RoleLevel.SALES_MANAGER,
        isAdminOnly: true,
        version: '1.0',
        lastUpdated: '2025-11-09T00:00:00.000Z',
        chapters: [
            {
                id: 'inventory-hub-overview',
                title: 'Inventory Hub Overview',
                description: 'Explore the unified Inventory Hub with analytics dashboard and inventory management',
                version: '1.0',
                lastUpdated: '2025-11-09T00:00:00.000Z',
                tour: TOURS.INVENTORY_HUB_SHOWCASE,
                estimatedMinutes: 8
            }
        ],
        totalSteps: 7,
        totalChapters: 1,
        comingSoon: false,
        featured: true,
        new: true,
        badge: {
            id: 'inventory-manager-badge',
            name: 'Inventory Manager',
            description: 'Mastered Inventory Hub operations',
            icon: 'FaGem',
            tier: BadgeTier.GOLD,
            category: TutorialCategory.ADMIN_ONLY
        }
    }
];
export default LearningCenter;
